import { useToast } from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import CDropdownMenu from '../../../../../../components/CDropdownMenu'
import DefaultModal from '../../../../../../components/DefaultModal'
import DropdownCell from '../../../../../../components/DropdownCell'
import Icon from '../../../../../../components/Icon'
import Switch from '../../../../../../components/Switch'
import {
  useGetCurrenciesQuery,
  useUpdateNetworkMutation,
} from '../../../../../../redux/services/coreApi'
import type {
  CurrencyDto,
  NetworkDto,
} from '../../../../../../types/coreApi-types'

interface EditNetworkModalProps {
  isOpen: boolean
  onClose: () => void
  network: NetworkDto | null
}

const EditNetworkModal: React.FC<EditNetworkModalProps> = ({
  isOpen,
  onClose,
  network,
}) => {
  const toast = useToast()
  const { data: currencies } = useGetCurrenciesQuery()

  const [name, setName] = useState('')
  const [isoCode, setIsoCode] = useState<string | null>(null)
  const [active, setActive] = useState(false)
  const [nativeCurrencyId, setNativeCurrencyId] = useState<string | null>(null)
  const [selectedCryptoCurrency, setSelectedCryptoCurrency] =
    useState<CurrencyDto | null>(null)

  const [updateNetwork, { isLoading }] = useUpdateNetworkMutation()

  useEffect(() => {
    if (network) {
      setName(network.name)
      setIsoCode(network.isoCode || null)
      // Because we get network from table which maps boolean to string "Active" or "Inactive"
      // @ts-expect-error
      setActive(network.active === 'Active')

      if (network.nativeCurrency) {
        setNativeCurrencyId(network.nativeCurrency.id)
        setSelectedCryptoCurrency(network.nativeCurrency)
      } else {
        setNativeCurrencyId(null)
        setSelectedCryptoCurrency(null)
      }
    }
  }, [network])

  const handleSubmit = async () => {
    if (!network) return

    try {
      await updateNetwork({
        id: network.id,
        name,
        isoCode,
        active,
        nativeCurrencyId,
      }).unwrap()

      toast({
        title: 'Success',
        description: 'Network updated successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      })

      onClose()
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to update network',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  // Filter for only cryptocurrencies
  const cryptoCurrencies =
    currencies?.filter(
      currency => currency.currencyType === 'CryptoCurrency'
    ) || []

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      isLoading={isLoading}
      isLoadingText="Updating network..."
      title="Edit Network"
    >
      <div className="p-5 pt-0 space-y-5">
        {network && (
          <>
            <div className="flex items-center gap-2 mb-5">
              <Icon src={network} size="md" />
              <div>
                <p className="text-default-base font-medium">{network.name}</p>
              </div>
            </div>

            <div>
              <p className="mb-1 text-description">Network Name</p>
              <input
                type="text"
                className="input-card w-full"
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>

            <div>
              <p className="mb-1 text-description">ISO Code</p>
              <input
                type="text"
                className="input-card w-full"
                value={isoCode || ''}
                onChange={e => setIsoCode(e.target.value || null)}
                placeholder="Optional"
              />
            </div>

            <div>
              <CDropdownMenu
                label="Native Currency"
                emptyText="Select a cryptocurrency"
                selectedText={selectedCryptoCurrency?.name}
                selectedIconSrc={selectedCryptoCurrency}
                renderList={cryptoCurrencies.map(currency => (
                  <DropdownCell
                    key={currency.id}
                    title={currency.name}
                    subtitle={currency.isoCode}
                    onClick={() => {
                      setNativeCurrencyId(currency.id)
                      setSelectedCryptoCurrency(currency)
                    }}
                    isActive={currency.id === selectedCryptoCurrency?.id}
                    imageName={currency}
                  />
                ))}
              />
            </div>

            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setActive(!active)}
            >
              <p className="text-description">Active Status</p>
              <Switch isOn={active} />
            </div>

            <button
              onClick={handleSubmit}
              className="button w-full mt-6"
              disabled={!name.trim()}
            >
              Save Changes
            </button>
          </>
        )}
      </div>
    </DefaultModal>
  )
}

export default EditNetworkModal
